import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from '@/components/ui/sonner'
import { AuthProvider, TenantProvider, UserPreferencesProvider } from '@/contexts'
import { SyncProvider } from '@/components/sync'
import { ProtectedRoute, PublicRoute, AdminGuard } from '@/components/auth'
import { AppLayout, LocationLayout } from '@/components/layout'
import { RootRedirect, LegacyRedirect } from '@/components/routing'

import {
  DashboardPage,
  LocationsPage,
  AircraftTypesPage,
  AircraftPage,
  ClientsPage,
  AreasPage,
  UnitsPage,
  LeasesPage,
  TransientStaysPage,
  FuelTrackingPage,
  RampPage,
  AllocationsPage,
  SpaceConflictsPage,
  SpaceContributionsPage,
  SpaceManagementPage,
  ComingSoonPage,
  TodayBoardPage,
  LoginPage,
  SignupPage,
  BuildingEditorPage,
} from '@/pages'
import { OpsDashboardMock, DashboardMVPMock } from '@/demo/dashboard'
import { CardHeaderDemos, CardHeaderRefinedDemos } from '@/demo/headers'
import { DragTestPage } from '@/demo/drag-test'
import { SpaceRevenueReportPage } from '@/demo/reports/SpaceRevenueReportPage'
import { SpaceYieldReportPage } from '@/demo/reports/SpaceYieldReportPage'
import { BuildingMetricsVariationsPage } from '@/demo/reports/BuildingMetricsVariationsPage'
import { SpaceYieldPage } from '@/pages/reports/SpaceYieldPage'
import { MovementTimelinePage, MovementCardDemos } from '@/demo/today'
import { DemoIndexPage } from '@/demo'
import {
  OrganizationSettingsPage,
  UserProfilePage,
  TeamSettingsPage,
  LocationPermissionsPage,
  LocationSettingsPage,
  IntegrationSettingsPage,
  IntegrationDetailPage,
} from '@/pages/settings'
import { PricingSettingsPage, PolicyDetailPage } from '@/components/settings/pricing'
import { SelectOrAddNewVariationsPage } from '@/demo/forms/SelectOrAddNewVariationsPage'
import { PlannerDemoPage } from '@/demo/5d-planner'
import { MastersPlannerPage } from '@/demo/masters-planner'
import { CinematicStudioPage } from '@/demo/cinematic'

// Create a client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false, // Prevent map reload on tab switch
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <TenantProvider>
            <SyncProvider>
            <UserPreferencesProvider>
              <Routes>
                {/* Public routes - accessible only when NOT logged in */}
                <Route element={<PublicRoute />}>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                </Route>

                {/* Protected routes - require authentication */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<AppLayout />}>
                    {/* Root redirect - smart routing based on locations */}
                    <Route path="/" element={<RootRedirect />} />

                    {/* ============================================ */}
                    {/* ORG-WIDE ROUTES (Reserved Keywords)         */}
                    {/* These must be defined BEFORE /:locationSlug */}
                    {/* ============================================ */}

                    {/* Portfolio Dashboard */}
                    <Route path="/dashboard" element={<DashboardPage />} />

                    {/* Master Data (Org-Wide) */}
                    <Route path="/clients" element={<ClientsPage />} />
                    <Route path="/aircrafts" element={<AircraftPage />} />
                    <Route path="/aircraft" element={<Navigate to="/aircrafts" replace />} />
                    <Route path="/leases" element={<LeasesPage />} />
                    <Route path="/locations" element={<LocationsPage />} />

                    {/* Legacy route redirect */}
                    <Route path="/clients/contacts" element={<Navigate to="/contacts" replace />} />

                    {/* Contracts sub-routes */}
                    <Route path="/leases/expiring" element={<ComingSoonPage />} />
                    <Route path="/leases/renewals" element={<ComingSoonPage />} />
                    <Route path="/leases/templates" element={<ComingSoonPage />} />

                    {/* Billing routes */}
                    <Route path="/invoices" element={<ComingSoonPage />} />
                    <Route path="/payments" element={<ComingSoonPage />} />
                    <Route path="/ar-aging" element={<ComingSoonPage />} />

                    {/* Reports routes */}
                    <Route path="/reports/occupancy" element={<ComingSoonPage />} />
                    <Route path="/reports/availability" element={<ComingSoonPage />} />
                    <Route path="/reports/capacity" element={<ComingSoonPage />} />
                    <Route path="/reports/revenue" element={<ComingSoonPage />} />
                    <Route path="/reports/space-revenue" element={<SpaceRevenueReportPage />} />
                    <Route path="/reports/space-yield" element={<SpaceYieldPage />} />

                    {/* Settings routes */}
                    <Route path="/settings" element={<Navigate to="/settings/organization" replace />} />
                    <Route path="/settings/organization" element={<OrganizationSettingsPage />} />
                    <Route path="/settings/profile" element={<UserProfilePage />} />
                    <Route path="/settings/users" element={<TeamSettingsPage />} />
                    <Route path="/settings/locations" element={<LocationPermissionsPage />} />
                    <Route path="/settings/pricing/policies/:policyId" element={<PolicyDetailPage />} />
                    <Route path="/settings/pricing" element={<PricingSettingsPage />} />
                    <Route path="/settings/integrations/:credentialSetId" element={<IntegrationDetailPage />} />
                    <Route path="/settings/integrations" element={<IntegrationSettingsPage />} />

                    {/* Admin-only routes */}
                    <Route element={<AdminGuard />}>
                      <Route path="/admin/aircraft-types" element={<AircraftTypesPage />} />
                    </Route>

                    {/* ============================================ */}
                    {/* LEGACY REDIRECTS                            */}
                    {/* Old flat routes → new location-scoped       */}
                    {/* ============================================ */}
                    <Route path="/operations/hangar-view" element={<LegacyRedirect suffix="ramp" />} />
                    <Route path="/operations/ramp" element={<LegacyRedirect suffix="ramp" />} />
                    <Route path="/operations/schedule" element={<LegacyRedirect suffix="schedule" />} />
                    <Route path="/builder" element={<LegacyRedirect suffix="builder" />} />
                    <Route path="/areas" element={<LegacyRedirect suffix="areas" />} />
                    <Route path="/units" element={<LegacyRedirect suffix="units" />} />
                    <Route path="/allocations" element={<LegacyRedirect suffix="allocations" />} />
                    <Route path="/conflicts" element={<LegacyRedirect suffix="conflicts" />} />
                    <Route path="/transients" element={<LegacyRedirect suffix="transients" />} />
                    <Route path="/quick-fit" element={<LegacyRedirect suffix="quick-fit" />} />
                    <Route path="/space-pool" element={<LegacyRedirect suffix="space-pool" />} />
                    <Route path="/space-contributions" element={<LegacyRedirect suffix="space-contributions" />} />
                    <Route path="/space" element={<LegacyRedirect suffix="space" />} />
                    <Route path="/vacant" element={<LegacyRedirect suffix="vacant" />} />

                    {/* Legacy building routes */}
                    <Route path="/buildings/hangars" element={<LegacyRedirect suffix="areas" />} />
                    <Route path="/buildings/offices" element={<LegacyRedirect suffix="areas" />} />
                    <Route path="/buildings/units" element={<LegacyRedirect suffix="units" />} />
                    <Route path="/hangars" element={<LegacyRedirect suffix="areas" />} />

                    {/* Legacy misc routes */}
                    <Route path="/map" element={<ComingSoonPage />} />
                    <Route path="/availability" element={<LegacyRedirect suffix="availability" />} />
                    <Route path="/waitlist" element={<LegacyRedirect suffix="waitlist" />} />

                    {/* ============================================ */}
                    {/* LOCATION-SCOPED ROUTES                      */}
                    {/* /:locationSlug/*                             */}
                    {/* ============================================ */}
                    <Route path="/:locationSlug" element={<LocationLayout />}>
                      {/* Location Dashboard */}
                      <Route index element={<Navigate to="dashboard" replace />} />
                      <Route path="dashboard" element={<DashboardPage />} />

                      {/* Operations */}
                      <Route path="ramp" element={<RampPage />} />
                      {/* Masters Event Planner — experimental demo (full-bleed) */}
                      <Route path="event-planner" element={<MastersPlannerPage />} />
                      {/* Cinematic Studio — promo playground (full-bleed) */}
                      <Route path="cinematic" element={<CinematicStudioPage />} />
                      {/* Legacy route - redirect old hangar-view to ramp */}
                      <Route path="hangar-view" element={<RampPage />} />
                      <Route path="schedule" element={<TodayBoardPage />} />
                      <Route path="allocations" element={<AllocationsPage />} />
                      <Route path="conflicts" element={<SpaceConflictsPage />} />

                      {/* Buildings & Areas */}
                      <Route path="builder" element={<BuildingEditorPage />} />
                      <Route path="areas" element={<AreasPage />} />
                      <Route path="units" element={<UnitsPage />} />

                      {/* Space Management */}
                      <Route path="space" element={<SpaceManagementPage />} />
                      <Route path="vacant" element={<ComingSoonPage />} />
                      <Route path="space-pool" element={<ComingSoonPage />} />
                      <Route path="space-contributions" element={<SpaceContributionsPage />} />

                      {/* Reports */}
                      <Route path="reports/space-yield" element={<SpaceYieldPage />} />

                      {/* Bookings */}
                      <Route path="transients" element={<TransientStaysPage />} />
                      <Route path="fuel" element={<FuelTrackingPage />} />
                      <Route path="quick-fit" element={<ComingSoonPage />} />
                      <Route path="availability" element={<ComingSoonPage />} />
                      <Route path="waitlist" element={<ComingSoonPage />} />

                      {/* Clients (Location-Scoped) */}
                      <Route path="clients" element={<ClientsPage />} />
                      <Route path="aircrafts" element={<AircraftPage />} />
                      <Route path="aircraft" element={<Navigate to="aircrafts" replace />} />
                      <Route path="leases" element={<LeasesPage />} />
                      <Route path="invoices" element={<ComingSoonPage />} />
                      <Route path="payments" element={<ComingSoonPage />} />

                      {/* Location Settings */}
                      <Route path="settings" element={<Navigate to="general" replace />} />
                      <Route path="settings/general" element={<LocationSettingsPage />} />
                      <Route path="settings/users" element={<LocationSettingsPage />} />
                      <Route path="settings/pricing" element={<LocationSettingsPage />} />
                      <Route path="settings/integrations" element={<LocationSettingsPage />} />
                      <Route path="settings/import" element={<LocationSettingsPage />} />

                      {/* Catch-all for unknown location routes */}
                      <Route path="*" element={<Navigate to="dashboard" replace />} />
                    </Route>
                  </Route>
                </Route>

                {/* Debug/Test routes - accessible without auth */}
                <Route path="/demo" element={<DemoIndexPage />} />
                <Route path="/demo/drag-test" element={<DragTestPage />} />
                <Route path="/demo/card-headers" element={<CardHeaderDemos />} />
                <Route path="/demo/card-headers-refined" element={<CardHeaderRefinedDemos />} />
                <Route path="/demo/movement-timeline" element={<MovementTimelinePage />} />
                <Route path="/demo/building-metrics-variations" element={<BuildingMetricsVariationsPage />} />
                <Route path="/demo/select-or-add-new" element={<SelectOrAddNewVariationsPage />} />
                <Route path="/demo/5d-planner" element={<PlannerDemoPage />} />
                <Route path="/reports/space-revenue" element={<SpaceRevenueReportPage />} />
                <Route path="/demo/space-yield" element={<SpaceYieldReportPage />} />
                
                <Route path="/ops-dashboard" element={<OpsDashboardMock />} />
                <Route path="/dashboard-mvp" element={<DashboardMVPMock />} />
                <Route path="/demo/movement-timeline" element={<MovementTimelinePage />} />
                <Route path="/demo/movement-cards" element={<MovementCardDemos />} />

                {/* Catch-all redirect */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
              <Toaster position="top-right" />
            </UserPreferencesProvider>
            </SyncProvider>
          </TenantProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
